"use client";

import { useEffect, useState } from "react";
import type { Lang } from "@/lib/types";

import { CcSiteChromeOverlay } from "@/components/layout/CcSiteChromeOverlay";
import { STALE_TAB_OVERLAY_ATTRIBUTE, STALE_TAB_OVERLAY_FADE_MS } from "@/lib/stale-tab-overlay";
import {
  installStaleTabRecovery,
  STALE_TAB_RECOVERY_RESET_EVENT,
  STALE_TAB_RELOAD_EVENT,
} from "@/lib/stale-tab-recovery";

export function CcStaleDeployRecovery({ lang }: { lang?: Lang }) {
  const [reloading, setReloading] = useState(false);
  const [exiting, setExiting] = useState(false);
  const present = reloading || exiting;

  // Keep the navigation and footer mounted behind the fading cover.
  useEffect(() => {
    document.documentElement.classList.toggle("cc-site--reload-covered", present);
    return () => document.documentElement.classList.remove("cc-site--reload-covered");
  }, [present]);

  useEffect(() => {
    let active = false;
    let exitTimer: ReturnType<typeof setTimeout> | undefined;
    let firstFrame = 0;
    let secondFrame = 0;
    const cancelExit = () => {
      clearTimeout(exitTimer);
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
    const onReload = () => {
      cancelExit();
      document.documentElement.removeAttribute(STALE_TAB_OVERLAY_ATTRIBUTE);
      active = true;
      setExiting(false);
      setReloading(true);
    };
    const onReset = () => {
      if (!active) return;
      cancelExit();
      active = false;
      document.documentElement.removeAttribute(STALE_TAB_OVERLAY_ATTRIBUTE);
      setReloading(false);
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      setExiting(!reduced);
      if (!reduced) exitTimer = setTimeout(() => setExiting(false), STALE_TAB_OVERLAY_FADE_MS + 32);
    };
    window.addEventListener(STALE_TAB_RELOAD_EVENT, onReload);
    window.addEventListener(STALE_TAB_RECOVERY_RESET_EVENT, onReset);

    if (document.documentElement.hasAttribute(STALE_TAB_OVERLAY_ATTRIBUTE)) {
      active = true;
      firstFrame = requestAnimationFrame(() => {
        setReloading(true);
        secondFrame = requestAnimationFrame(onReset);
      });
    }

    const cleanupListeners = installStaleTabRecovery();
    return () => {
      window.removeEventListener(STALE_TAB_RELOAD_EVENT, onReload);
      window.removeEventListener(STALE_TAB_RECOVERY_RESET_EVENT, onReset);
      cleanupListeners();
      cancelExit();
    };
  }, []);

  return <CcSiteChromeOverlay lang={lang} visible={reloading} present={present} />;
}
